import { CREDIT_ACCOUNT_MIGRATION_STATUS, CREDIT_ACCOUNT_STATUS } from 'constants/creditAccount';
import moment from 'moment';
import { isOnOrAfterWalletGoLive, isUkCorporationCreatedAfterWalletGoLive } from 'utils/walletUtil';

const toCamelCase = string => string?.charAt(0).toLowerCase() + string?.slice(1);

const creditAccountMigrationConfigManager = ({ corporation, override = null }) => {
  if (!corporation) return null;

  // Net new UK orgs cannot have an Allstar credit account, so its page and onboarding step are hidden
  const isCreatedAfterWalletGoLive = isUkCorporationCreatedAfterWalletGoLive({
    country: corporation.country,
    createdAt: corporation.createdAt ?? null,
  });

  const creditAccountMigrationStatus = toCamelCase(corporation.creditAccountMigrationStatus);
  const creditAccountStatus = toCamelCase(corporation.creditAccountStatus);

  // From the wallet go-live no new Allstar account can be created. Any org that already has one
  // (any credit account status) is grandfathered and keeps it, including a UK org created after
  // the go-live through the old flow; the others lose every way to set one up.
  const isCreditAccountUnavailable =
    !creditAccountStatus && (isCreatedAfterWalletGoLive || isOnOrAfterWalletGoLive());

  const isStatus = (status, expected) => status === expected;
  const isAnyStatus = (status, expectedStatuses) => expectedStatuses.includes(status);

  const isCreditAccountMigrationNew = isStatus(
    creditAccountMigrationStatus,
    CREDIT_ACCOUNT_MIGRATION_STATUS.NEW,
  );

  const isCreditAccountMigrationStarted =
    !!creditAccountStatus &&
    isStatus(creditAccountMigrationStatus, CREDIT_ACCOUNT_MIGRATION_STATUS.STARTED);

  const isCreditAccountMigrationNotStarted =
    !creditAccountMigrationStatus ||
    creditAccountMigrationStatus === CREDIT_ACCOUNT_MIGRATION_STATUS.NOT_STARTED;

  const isCreditAccountStatusActive = isStatus(creditAccountStatus, CREDIT_ACCOUNT_STATUS.ACTIVE);
  const isCreditAccountStatusPending = isStatus(creditAccountStatus, CREDIT_ACCOUNT_STATUS.PENDING);
  const isCreditAccountStatusRejected = isStatus(
    creditAccountStatus,
    CREDIT_ACCOUNT_STATUS.REJECTED,
  );

  const isCreditAccountStatusOther =
    !!creditAccountStatus && !Object.values(CREDIT_ACCOUNT_STATUS).includes(creditAccountStatus);

  const isNewBadgeDisplayed = isCreditAccountMigrationStarted && isCreditAccountStatusPending;
  const isCreditAccountPageBlocked =
    isCreditAccountUnavailable ||
    (!isCreditAccountMigrationStarted && !isCreditAccountMigrationNew);
  const isInvoicesPageBlocked =
    isCreditAccountMigrationNotStarted ||
    isAnyStatus(creditAccountStatus, [
      CREDIT_ACCOUNT_STATUS.CLOSED,
      CREDIT_ACCOUNT_STATUS.REJECTED,
    ]);
  const isCreditAccountPendingBannerVisible = isCreditAccountStatusPending;
  const isCreditAccountRejectedBannerVisible = isCreditAccountStatusRejected;
  const isCreditAccountBannerVisible =
    !isCreditAccountUnavailable &&
    isCreditAccountMigrationStarted &&
    !isCreditAccountStatusRejected;
  const isCreditAccountInfoModalVisible =
    override?.isCreditAccountInfoModalVisible ??
    (!isCreditAccountUnavailable && isCreditAccountMigrationStarted && !creditAccountStatus);

  // An org that cannot get a credit account keeps its payment methods
  const isPaymentMethodsPageBlocked =
    !isCreditAccountUnavailable &&
    ((isCreditAccountMigrationNew &&
      !isCreditAccountStatusRejected &&
      !isCreditAccountStatusPending) ||
      (isCreditAccountMigrationStarted &&
        (isCreditAccountStatusActive || isCreditAccountStatusOther)));

  const isBillingPageReadonly =
    isAnyStatus(creditAccountStatus, [
      CREDIT_ACCOUNT_STATUS.ACTIVE,
      CREDIT_ACCOUNT_STATUS.ON_HOLD,
      CREDIT_ACCOUNT_STATUS.PENDING,
    ]) || isCreditAccountStatusOther;

  const isCreditAccountStepActive =
    !isCreditAccountUnavailable &&
    ((isCreditAccountMigrationNew && !isCreditAccountStatusRejected) ||
      (isCreditAccountMigrationStarted && !isCreditAccountStatusRejected));

  const isCreditAccountActivationDateTime = moment(corporation.creditAccountActivationDateTime);
  const endOfNextMonthFromActivation = isCreditAccountActivationDateTime
    .clone()
    .add(1, 'month')
    .endOf('month');
  const today = moment();

  const isReplaceMonthlyStatementsBannerVisible = Boolean(
    corporation.creditAccountActivationDateTime &&
      isCreditAccountStatusActive &&
      !isCreditAccountMigrationNew &&
      today.isBefore(endOfNextMonthFromActivation),
  );

  return {
    isCreditAccountInfoModalVisible,
    paymentMethods: {
      isPageBlocked: isPaymentMethodsPageBlocked,
      isCreditAccountBannerVisible,
    },
    creditAccount: {
      isNewBadgeDisplayed,
      isPageBlocked: isCreditAccountPageBlocked,
      isPendingBannerVisible: isCreditAccountPendingBannerVisible,
      isRejectedBannerVisible: isCreditAccountRejectedBannerVisible,
    },
    invoices: {
      isPageBlocked: isInvoicesPageBlocked,
    },
    billing: {
      isReadonly: isBillingPageReadonly,
    },
    onboarding: {
      isCreditAccountStepActive,
    },
    monthlyStatements: {
      isReplaceMonthlyStatementsBannerVisible,
    },
  };
};

export default creditAccountMigrationConfigManager;
